import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { StepUpSpinner } from "../../../Components/Loader";
import { useGetCourseQuery } from "../../../Api/attoDeskApi";
import { subjectInitials, subjectTint } from "../shared/subjectColor";
import { toEmbedUrl } from "../shared/videoUrl";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx, secondaryBtnSx } from "../shared/moduleSx";

type Material = {
  id: number;
  title?: string | null;
  description?: string | null;
  materialType?: string | null;
  filePath?: string | null;
  externalUrl?: string | null;
  orderNumber?: number | null;
  isPublished?: boolean | null;
};

type CourseDetail = {
  id: number;
  courseName: string;
  courseCode?: string | null;
  description?: string | null;
  status?: string | null;
  thumbnail?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  grade?: { id: number; grade: string } | null;
  subject?: { id: number; subject_name: string } | null;
  staff?: { id: number; teamFirstName?: string; teamLastName?: string } | null;
  materials?: Material[];
  materials_count?: number;
  assignments_count?: number;
};

// Two states, two treatments — Active picks up B13 primary; everything
// else is neutral. Matches the List page.
const statusChipStyle = (s?: string | null): { bg: string; fg: string } => {
  const v = (s || "").toLowerCase();
  if (v === "active") return { bg: tint12(B13.primary), fg: B13.primary };
  return { bg: tint12(B13Ink.ink3), fg: B13Ink.ink3 };
};

const metaChipSx = {
  height: 22,
  bgcolor: "transparent",
  border: `1px solid ${B13.line}`,
  color: B13Ink.ink2,
  fontSize: 11,
} as const;

/**
 * AttachmentPreviewDialog — inline preview for the browser-native
 * types the Course Attachments feature already supports (image, pdf).
 * Reuses the existing `filePath` URL emitted by CourseMaterialController::upload
 * (a public Storage::url()), so no new backend surface is required.
 *
 * Image  → <img>   inside a soft-neutral surface.
 * PDF    → <iframe> letting the browser's built-in viewer render it.
 * Other  → a friendly "no inline preview" hint + a Download button.
 *
 * Uses MUI Dialog so Escape-to-close, backdrop-click-to-close, focus
 * trap, and ARIA are all handled by the library. The Download action
 * inside the dialog uses the same `download` semantics as the row
 * button so the two views agree on what "save" means.
 */
const AttachmentPreviewDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  material: Material | null;
}> = ({ open, onClose, material }) => {
  // Reset the internal load state whenever the material identity
  // changes so the loader appears again for a fresh selection.
  const [loading, setLoading] = React.useState(true);
  const [errored, setErrored] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setLoading(true);
      setErrored(false);
    }
  }, [open, material?.id]);

  if (!material) return null;

  const type = (material.materialType || "").toLowerCase();
  const isImage = type === "image";
  const isPdf   = type === "pdf";
  const url = material.filePath || "";
  const filename = material.title || "Attachment";

  const HeaderIcon = isPdf ? PictureAsPdfIcon : isImage ? ImageIcon : DescriptionIcon;
  const iconTint  = isPdf ? "#DC2626" : isImage ? "#0EA5E9" : B13Ink.ink3;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-label={`Preview attachment ${filename}`}
      PaperProps={{
        sx: {
          width: "min(1000px, 92vw)",
          maxWidth: "92vw",
          maxHeight: "92vh",
          borderRadius: "16px",
          border: `1px solid ${B13.line}`,
          boxShadow: "0 24px 48px rgba(15,23,42,0.18)",
          overflow: "hidden",
          bgcolor: "#FFFFFF",
        },
      }}
    >
      {/* ── Header ────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2.25,
          py: 1.5,
          borderBottom: `1px solid ${B13.line}`,
          bgcolor: "#FFFFFF",
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            bgcolor: `${iconTint}14`,
            color: iconTint,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: 20 },
            flexShrink: 0,
          }}
        >
          <HeaderIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              ...B13TypeScale.bodyStrong,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={filename}
          >
            {filename}
          </Typography>
          <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink3, letterSpacing: 0.4, textTransform: "uppercase" }}>
            {type || "file"}
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            aria-label="Close attachment preview"
            onClick={onClose}
            size="small"
            sx={{
              color: B13Ink.ink3,
              "&:hover": { color: B13.primary, bgcolor: tint12(B13.primary) },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Body ──────────────────────────────────────────────── */}
      <DialogContent
        sx={{
          p: 0,
          bgcolor: "#F6F8FB",
          minHeight: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {/* Loading overlay — visible while the image or iframe fires
            its first load event. Sits above the frame so we don't
            leave the modal blank while the file streams in. */}
        {loading && !errored && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.25,
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <CircularProgress size={28} sx={{ color: B13.primary }} />
            <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink3 }}>
              Loading preview…
            </Typography>
          </Box>
        )}

        {errored || !url ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <ErrorOutlineOutlinedIcon sx={{ fontSize: 40, color: B13Ink.ink3, mb: 1 }} />
            <Typography sx={{ ...B13TypeScale.bodyStrong, mb: 0.5 }}>
              Unable to preview this attachment.
            </Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink3, mb: 2 }}>
              You can still download the file to view it locally.
            </Typography>
            {url && (
              <Button
                component="a"
                href={url}
                target="_blank"
                rel="noopener"
                download
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  bgcolor: B13.primary,
                  boxShadow: "0 4px 10px rgba(31,111,235,0.24)",
                  "&:hover": {
                    bgcolor: B13.primary,
                    filter: "brightness(0.95)",
                    boxShadow: "0 6px 14px rgba(31,111,235,0.30)",
                  },
                }}
              >
                Download file
              </Button>
            )}
          </Box>
        ) : isImage ? (
          <Box
            component="img"
            src={url}
            alt={filename}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setErrored(true);
            }}
            sx={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "75vh",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              // Anchor to top-left of the neutral surface so tall
              // images don't shift when the loader unmounts.
              position: "relative",
              zIndex: 2,
            }}
          />
        ) : isPdf ? (
          <Box
            component="iframe"
            src={url}
            title={filename}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setErrored(true);
            }}
            sx={{
              display: "block",
              width: "100%",
              height: "80vh",
              border: 0,
              bgcolor: "#FFFFFF",
              position: "relative",
              zIndex: 2,
            }}
          />
        ) : (
          // Fallback for any future material type that isn't natively
          // renderable in the browser. Reuses the same "no preview"
          // treatment as the error state, but branches off the
          // materialType check rather than a load failure.
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ ...B13TypeScale.bodyStrong, mb: 0.5 }}>
              No inline preview available for this file type.
            </Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink3, mb: 2 }}>
              Use Download to open the file in its native application.
            </Typography>
            <Button
              component="a"
              href={url}
              target="_blank"
              rel="noopener"
              download
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                bgcolor: B13.primary,
                "&:hover": { bgcolor: B13.primary, filter: "brightness(0.95)" },
              }}
            >
              Download file
            </Button>
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 2.25,
          py: 1.5,
          borderTop: `1px solid ${B13.line}`,
          bgcolor: "#FFFFFF",
        }}
      >
        {url && (
          <Button
            component="a"
            href={url}
            target="_blank"
            rel="noopener"
            download
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              px: 2,
              bgcolor: B13.primary,
              boxShadow: "0 4px 10px rgba(31,111,235,0.24)",
              "&:hover": {
                bgcolor: B13.primary,
                filter: "brightness(0.95)",
                boxShadow: "0 6px 14px rgba(31,111,235,0.30)",
              },
            }}
            aria-label={`Download ${filename}`}
          >
            Download
          </Button>
        )}
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            ...secondaryBtnSx,
            borderRadius: "10px",
            px: 2,
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

const CourseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useGetCourseQuery({ id: id ?? "" });
  // Attachment preview state — one modal shared by every row on the
  // page. Storing the whole Material lets the dialog show the same
  // icon / title / type it appears with in the row.
  const [previewMaterial, setPreviewMaterial] = useState<Material | null>(null);

  const course: CourseDetail | null = useMemo(() => {
    const arr = Array.isArray(data?.data) ? (data!.data as CourseDetail[]) : [];
    return arr.find((c) => String(c.id) === String(id)) ?? null;
  }, [data, id]);

  if (isLoading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <StepUpSpinner size="large" label="Loading course" />
      </Box>
    );
  }

  if (!course) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Course not found.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ ...secondaryBtnSx, mt: 2 }}
          variant="outlined"
          onClick={() => navigate("/course/list")}
        >
          Back to courses
        </Button>
      </Box>
    );
  }

  const materials = (course.materials ?? []).slice().sort(
    (a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0),
  );
  const videoMaterials = materials.filter((m) => (m.materialType || "").toLowerCase() === "video");
  const linkMaterials = materials.filter((m) => (m.materialType || "").toLowerCase() === "link");
  const attachmentMaterials = materials.filter((m) => {
    const t = (m.materialType || "").toLowerCase();
    return t === "pdf" || t === "image" || t === "document";
  });

  const tint = subjectTint(course.subject?.subject_name || course.courseName);
  const initials = subjectInitials(course.subject?.subject_name || course.courseName);
  const staffName = course.staff
    ? `${course.staff.teamFirstName ?? ""} ${course.staff.teamLastName ?? ""}`.trim()
    : "";
  const statusStyle = statusChipStyle(course.status);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      {/* Top bar */}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
        <IconButton
          onClick={() => navigate("/course/list")}
          sx={{ color: B13Ink.ink3, "&:hover": { color: B13.primary } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={B13TypeScale.caption}>Courses</Typography>
      </Stack>

      {/* Header surface — one clear focal point: name, subject/grade,
          status, staff above the fold; Edit sits top-right. */}
      <Box sx={{ ...surfaceSx, p: 0, overflow: "hidden", mb: 2 }}>
        <Box
          sx={{
            height: 120,
            bgcolor: tint.bg,
            color: tint.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: `1px solid ${B13.line}`,
          }}
        >
          {course.thumbnail ? (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img
              src={course.thumbnail}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Typography sx={{ fontSize: 44, fontWeight: 800 }}>{initials}</Typography>
          )}
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "flex-start" }}
            justifyContent="space-between"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={B13TypeScale.h1}>{course.courseName}</Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {course.courseCode ? (
                  <Chip label={course.courseCode} size="small" sx={metaChipSx} />
                ) : null}
                {course.grade?.grade ? (
                  <Chip label={`Grade ${course.grade.grade}`} size="small" sx={metaChipSx} />
                ) : null}
                {course.subject?.subject_name ? (
                  <Chip label={course.subject.subject_name} size="small" sx={metaChipSx} />
                ) : null}
                <Chip
                  label={(course.status ?? "").toString() || "—"}
                  size="small"
                  sx={{
                    height: 22,
                    textTransform: "capitalize",
                    fontWeight: 600,
                    fontSize: 11,
                    bgcolor: statusStyle.bg,
                    color: statusStyle.fg,
                    border: "none",
                  }}
                />
              </Stack>
              {staffName ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: 12,
                      bgcolor: tint12(B13.primary),
                      color: B13.primary,
                    }}
                  >
                    {(staffName[0] || "?").toUpperCase()}
                  </Avatar>
                  <Typography sx={B13TypeScale.caption}>{staffName}</Typography>
                </Stack>
              ) : null}
              {(course.startDate || course.endDate) && (
                <Typography sx={{ ...B13TypeScale.caption, display: "block", mt: 1 }}>
                  {course.startDate ? `Starts ${course.startDate}` : ""}
                  {course.startDate && course.endDate ? " · " : ""}
                  {course.endDate ? `Ends ${course.endDate}` : ""}
                </Typography>
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditSharpIcon />}
              onClick={() => navigate(`/course/edit/${course.id}`)}
              sx={secondaryBtnSx}
            >
              Edit course
            </Button>
          </Stack>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Left column: description + video + attachments */}
        <Grid item xs={12} md={8}>
          {/* Description */}
          <Box sx={{ ...surfaceSx, mb: 2 }}>
            <Typography sx={{ ...B13TypeScale.h3, mb: 1 }}>Description</Typography>
            {course.description ? (
              <Typography sx={{ ...B13TypeScale.body, whiteSpace: "pre-wrap" }}>
                {course.description}
              </Typography>
            ) : (
              <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink3 }}>
                No description yet.
              </Typography>
            )}
          </Box>

          {/* Video + links */}
          {(videoMaterials.length > 0 || linkMaterials.length > 0) && (
            <Box sx={{ ...surfaceSx, mb: 2 }}>
              <Typography sx={{ ...B13TypeScale.h3, mb: 1 }}>Video &amp; links</Typography>
              <Stack spacing={1.5}>
                {videoMaterials.map((m) => {
                  const embed = toEmbedUrl(m.externalUrl);
                  return (
                    <Box key={m.id}>
                      {embed ? (
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            paddingTop: "56.25%",
                            borderRadius: 1,
                            overflow: "hidden",
                            bgcolor: "#000",
                          }}
                        >
                          <iframe
                            title={m.title || "Video"}
                            src={embed}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              border: 0,
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            p: 1.5,
                            border: `1px solid ${B13.line}`,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <PlayCircleOutlineIcon sx={{ color: B13Ink.ink3 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ ...B13TypeScale.bodyStrong }} noWrap>
                              {m.title || "Video"}
                            </Typography>
                            {m.externalUrl ? (
                              <Link
                                href={m.externalUrl}
                                target="_blank"
                                rel="noopener"
                                sx={{
                                  ...B13TypeScale.caption,
                                  color: B13.primary,
                                  wordBreak: "break-all",
                                }}
                              >
                                {m.externalUrl}
                              </Link>
                            ) : null}
                          </Box>
                          {m.externalUrl ? (
                            <IconButton
                              size="small"
                              component="a"
                              href={m.externalUrl}
                              target="_blank"
                              rel="noopener"
                              sx={{ color: B13Ink.ink3, "&:hover": { color: B13.primary } }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Box>
                      )}
                    </Box>
                  );
                })}
                {linkMaterials.map((m) => (
                  <Box
                    key={m.id}
                    sx={{
                      p: 1.5,
                      border: `1px solid ${B13.line}`,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <OpenInNewIcon sx={{ color: B13Ink.ink3 }} fontSize="small" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ ...B13TypeScale.bodyStrong }} noWrap>
                        {m.title || "Link"}
                      </Typography>
                      {m.externalUrl ? (
                        <Link
                          href={m.externalUrl}
                          target="_blank"
                          rel="noopener"
                          sx={{
                            ...B13TypeScale.caption,
                            color: B13.primary,
                            wordBreak: "break-all",
                          }}
                        >
                          {m.externalUrl}
                        </Link>
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Attachments */}
          <Box sx={surfaceSx}>
            <Typography sx={{ ...B13TypeScale.h3, mb: 1 }}>Attachments</Typography>
            {attachmentMaterials.length === 0 ? (
              <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink3 }}>
                No attachments.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {attachmentMaterials.map((m) => {
                  const type = (m.materialType || "").toLowerCase();
                  const Icon =
                    type === "pdf"
                      ? PictureAsPdfIcon
                      : type === "image"
                      ? ImageIcon
                      : DescriptionIcon;
                  const iconTint =
                    type === "pdf" ? "#DC2626" : type === "image" ? "#0EA5E9" : B13Ink.ink3;
                  const hasFile = !!m.filePath;
                  const canPreview = hasFile && (type === "image" || type === "pdf");
                  return (
                    <Box
                      key={m.id}
                      sx={{
                        p: 1.25,
                        border: `1px solid ${B13.line}`,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        bgcolor: "#FFFFFF",
                        transition: "border-color 160ms ease, box-shadow 160ms ease",
                        "&:hover": {
                          borderColor: tint12(B13.primary),
                          boxShadow: "0 4px 12px rgba(15,23,42,0.05)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          bgcolor: `${iconTint}14`,
                          color: iconTint,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "& svg": { fontSize: 20 },
                          flexShrink: 0,
                        }}
                      >
                        <Icon />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={B13TypeScale.bodyStrong} noWrap title={m.title || undefined}>
                          {m.title || "Attachment"}
                        </Typography>
                        <Typography
                          sx={{
                            ...B13TypeScale.caption,
                            color: B13Ink.ink3,
                            textTransform: "uppercase",
                            letterSpacing: 0.4,
                          }}
                        >
                          {type || "file"}
                        </Typography>
                      </Box>
                      {/* Actions ─ compact View button (only when we
                          can actually preview) + icon-only Download.
                          The two are separate on-click paths so
                          preview never triggers a save-to-disk. */}
                      <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                        {canPreview && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                            onClick={() => setPreviewMaterial(m)}
                            aria-label={`View ${m.title || "attachment"}`}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              fontSize: 12.5,
                              borderRadius: "8px",
                              px: 1.25,
                              py: 0.35,
                              minHeight: 32,
                              color: B13.primary,
                              borderColor: tint12(B13.primary),
                              bgcolor: "#FFFFFF",
                              "&:hover": {
                                bgcolor: tint12(B13.primary),
                                borderColor: B13.primary,
                                color: B13.primary,
                              },
                            }}
                          >
                            View
                          </Button>
                        )}
                        {hasFile && (
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              component="a"
                              href={m.filePath!}
                              target="_blank"
                              rel="noopener"
                              download
                              aria-label={`Download ${m.title || "attachment"}`}
                              sx={{
                                color: B13Ink.ink3,
                                "&:hover": { color: B13.primary, bgcolor: tint12(B13.primary) },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Grid>

        {/* Right column: assignments + deliberate omissions */}
        <Grid item xs={12} md={4}>
          <Box sx={surfaceSx}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <AssignmentOutlinedIcon fontSize="small" sx={{ color: B13Ink.ink3 }} />
              <Typography sx={B13TypeScale.h3}>Assignments</Typography>
              <Chip
                label={course.assignments_count ?? 0}
                size="small"
                sx={{
                  ml: "auto",
                  height: 20,
                  fontSize: 11,
                  bgcolor: tint12(B13.primary),
                  color: B13.primary,
                  border: "none",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            </Stack>
            <Divider sx={{ mb: 1.5, borderColor: B13.line }} />
            {(course.assignments_count ?? 0) === 0 ? (
              <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink3 }}>
                No assignments have been created for this course yet.
              </Typography>
            ) : (
              <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2 }}>
                {course.assignments_count} assignment(s) linked. Open the Assignments module for details.
              </Typography>
            )}
          </Box>

          {/* Deliberate omissions — a hairline note, not a coloured card. */}
          <Box sx={{ ...surfaceSx, mt: 2, bgcolor: B13.canvas }}>
            <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink2 }}>
              <strong>Not shown:</strong> linked classes and enrolled students.
              These need a direct relationship between classes and courses that
              does not exist in the schema today. When that relationship lands,
              both panels can be added here.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Attachment preview dialog — mounted at the page root so it
          overlays everything (matches MUI Dialog default portal). One
          instance shared by every row; the currently-viewed material
          drives its content. Closing clears the selection so a stale
          Material can never render on the next open. */}
      <AttachmentPreviewDialog
        open={!!previewMaterial}
        material={previewMaterial}
        onClose={() => setPreviewMaterial(null)}
      />
    </Box>
  );
};

export default CourseDetail;
